import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Button,
    Box,
    Typography,
    Alert,
} from "@mui/material";
import NavBar from "../navbar";
import { Link, useNavigate } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc } from "../utc";

const Plans = () => {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) {
            setError(validate);
            return;
        }

        const getPlans = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + "/admin/plans", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status} ${response.statusText}`);
                }
                const jsonData = await response.json();
                setData(jsonData);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            }
        };

        getPlans();
    }, []);

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };

    const sortData = (rows, orderBy, order) => {
        return rows.sort((a, b) => {
            const valueA = a[orderBy];
            const valueB = b[orderBy];

            if (typeof valueA === "string" && typeof valueB === "string") {
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            if (typeof valueA === "number" && typeof valueB === "number") {
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            const dateA = new Date(valueA);
            const dateB = new Date(valueB);
            return order === "asc" ? dateA - dateB : dateB - dateA;
        });
    };

    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{error}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <CircularProgress />
            </div>
        );
    }

    const sortedRows = data.length > 0 ? sortData(data, orderBy, order) : [];

    return (
        <>
            <NavBar />
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" component="h1">
                        Subscription Plans
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/plans/new")}
                    >
                        Create New Plan
                    </Button>
                </Box>

                {data.length === 0 ? (
                    <Alert severity="info">
                        No plans found. Click "Create New Plan" to add your first subscription plan.
                    </Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="plans table">
                            <TableHead>
                                <TableRow>
                                    <SortableTableCell column="id" label="ID" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="name" label="Name" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="usage_context" label="Usage Context" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="max_keys" label="Max Keys" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="default_period" label="Default Period" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="default_count" label="Default Count" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="ten_second_limit" label="Ten Second Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="minutely_limit" label="Minutely Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="hourly_limit" label="Hourly Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="daily_limit" label="Daily Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="monthly_limit" label="Monthly Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="five_hour_limit" label="Session (5h) Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="weekly_limit" label="Weekly Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="linkedin_five_hour_limit" label="LinkedIn (5h) Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="linkedin_weekly_limit" label="LinkedIn (weekly) Limit" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="created_at" label="Created At" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <SortableTableCell column="modified_at" label="Modified At" orderBy={orderBy} order={order} handleSort={handleSort} />
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedRows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.usage_context || 'standard'}</TableCell>
                                        <TableCell>{row.max_keys}</TableCell>
                                        <TableCell>{row.default_period} days</TableCell>
                                        <TableCell>{row.default_count} points</TableCell>
                                        <TableCell>{row.ten_second_limit !== null ? row.ten_second_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.minutely_limit !== null ? row.minutely_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.hourly_limit !== null ? row.hourly_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.daily_limit !== null ? row.daily_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.monthly_limit !== null ? row.monthly_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.five_hour_limit !== null ? row.five_hour_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.weekly_limit !== null ? row.weekly_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.linkedin_five_hour_limit !== null ? row.linkedin_five_hour_limit : 'No limit'}</TableCell>
                                        <TableCell>{row.linkedin_weekly_limit !== null ? row.linkedin_weekly_limit : 'No limit'}</TableCell>
                                        <TableCell>{formatUtc(row.created_at)}</TableCell>
                                        <TableCell>{formatUtc(row.modified_at)}</TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => navigate(`/plans/${row.id}/edit`)}
                                            >
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </>
    );
};

const SortableTableCell = ({ column, label, orderBy, order, handleSort }) => (
    <TableCell onClick={() => handleSort(column)} style={{ cursor: "pointer" }}>
        {label} {orderBy === column && (order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
    </TableCell>
);

export default Plans;
